import React, { useMemo, useState, useEffect } from "react";
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { slotsForModule, MODULES, normalizeModule } from "@/lib/modules";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import SoccerPitch from "@/components/SoccerPitch";
import PlayerJersey from "@/components/PlayerJersey";

const ROLE_COLORS = {
  PT: "#f59e0b",
  DEF: "#06b6d4",
  CEN: "#ccff00",
  ATT: "#f97316",
};

function PlayerChip({ player, dragId, compact = false, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
    data: { player },
  });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    // When dragging, we hide the source chip (DragOverlay renders the visual copy
    // in a portal so it can escape the roster panel's `overflow-y: auto` clipping).
    opacity: isDragging ? 0 : 1,
    borderColor: `${ROLE_COLORS[player.role]}55`,
    touchAction: "none",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`player-chip select-none card-surface rounded-lg px-2 py-1.5 flex items-center gap-2 cursor-grab active:cursor-grabbing ${compact ? "text-xs" : "text-sm"}`}
      data-testid={`player-${player.id}`}
    >
      <span className="text-[10px] font-black px-1.5 py-0.5 rounded"
        style={{ background: `${ROLE_COLORS[player.role]}22`, color: ROLE_COLORS[player.role] }}
      >
        {player.role}
      </span>
      <span className="text-slate-900 truncate flex-1">{player.name}</span>
      {player.team && !compact && <span className="text-[10px] text-slate-400">{player.team}</span>}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="text-slate-400 hover:text-slate-900"
          data-testid={`remove-${player.id}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function PitchSlot({ id, role, x, y, player, onRemove, invalid }) {
  const { setNodeRef, isOver } = useDroppable({ id, data: { role } });
  return (
    <div
      ref={setNodeRef}
      style={{ left: `${x}%`, top: `${y}%` }}
      className={`absolute -translate-x-1/2 -translate-y-1/2 transition-transform ${isOver ? "scale-110" : ""}`}
      data-testid={`slot-${id}`}
    >
      {player ? (
        <PlayerJersey player={player} role={role} size={44} onRemove={onRemove} />
      ) : (
        <PlayerJersey role={role} size={44} empty invalid={invalid} />
      )}
    </div>
  );
}

export default function LineupBuilder({ team, onSaved }) {
  const [module, setModule] = useState(normalizeModule(team?.lineup?.module));
  const [starters, setStarters] = useState(team?.lineup?.starters || { PT: [], DEF: [], CEN: [], ATT: [] });
  const [busy, setBusy] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null); // {player} | null

  useEffect(() => {
    if (team?.lineup) {
      setModule(normalizeModule(team.lineup.module));
      // Se il DB salva {} vuoto (populate/simulate) fornisci lo scheletro
      const s = team.lineup.starters;
      const hasKeys = s && (s.PT !== undefined || s.DEF !== undefined || s.CEN !== undefined || s.ATT !== undefined);
      setStarters(hasKeys ? s : { PT: [], DEF: [], CEN: [], ATT: [] });
    }
  }, [team?.user_id]);

  const roster = team?.roster || [];
  const byId = useMemo(() => Object.fromEntries(roster.map((p) => [p.id, p])), [roster]);
  const slots = useMemo(() => slotsForModule(module), [module]);

  // Build slot-to-player map
  const slotPlayers = useMemo(() => {
    const map = {};
    for (const s of slots) {
      const slotId = `${s.role}-${s.index}`;
      const pid = (starters[s.role] || [])[s.index];
      map[slotId] = pid ? byId[pid] : null;
    }
    return map;
  }, [slots, starters, byId]);

  const assignedIds = new Set(Object.values(slotPlayers).filter(Boolean).map((p) => p.id));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
  );

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over) return;
    const player = active.data.current?.player;
    if (!player) return;
    const [role, idxStr] = String(over.id).split("-");
    const index = parseInt(idxStr, 10);
    if (role !== player.role) {
      toast.error(`Il giocatore è ${player.role}, non può occupare uno slot ${role}`);
      return;
    }
    setStarters((prev) => {
      // remove player wherever assigned in same role
      const next = { PT: [...(prev.PT || [])], DEF: [...(prev.DEF || [])], CEN: [...(prev.CEN || [])], ATT: [...(prev.ATT || [])] };
      // remove player from any slot in any role
      for (const r of ["PT", "DEF", "CEN", "ATT"]) {
        next[r] = next[r].map((pid) => (pid === player.id ? null : pid));
      }
      next[role][index] = player.id;
      return next;
    });
  };

  const removeAt = (role, index) => {
    setStarters((prev) => {
      const next = { ...prev, [role]: [...(prev[role] || [])] };
      next[role][index] = null;
      return next;
    });
  };

  const save = async () => {
    // Validate: all slots must be filled per module
    const cfg = MODULES[module] || MODULES["442"];
    const missing = { PT: 0, DEF: 0, CEN: 0, ATT: 0 };
    const required = { PT: 1, DEF: cfg.DEF, CEN: cfg.CEN, ATT: cfg.ATT };
    for (const role of ["PT", "DEF", "CEN", "ATT"]) {
      const filled = (starters[role] || []).slice(0, required[role]).filter(Boolean).length;
      missing[role] = Math.max(0, required[role] - filled);
    }
    const totalMissing = Object.values(missing).reduce((a, b) => a + b, 0);
    if (totalMissing > 0) {
      const parts = Object.entries(missing).filter(([, n]) => n > 0).map(([r, n]) => `${n} ${r}`);
      toast.error(`Formazione incompleta: mancano ${parts.join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const cleaned = {
        PT: (starters.PT || []).slice(0, 1),
        DEF: (starters.DEF || []).slice(0, cfg.DEF),
        CEN: (starters.CEN || []).slice(0, cfg.CEN),
        ATT: (starters.ATT || []).slice(0, cfg.ATT),
      };
      await api.post("/me/lineup", { module, starters: cleaned });
      toast.success("Formazione salvata!");
      onSaved && onSaved({ module, starters: cleaned });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setBusy(false);
    }
  };

  // Compute missing per role for badge
  // 3-in-1 defensive: se per qualche motivo cfg è undefined (modulo custom non valido),
  // fallback silenzioso al 442 così la Dashboard non va in blank-screen.
  const cfg = MODULES[module] || MODULES["442"];
  const requiredCounts = { PT: 1, DEF: cfg.DEF, CEN: cfg.CEN, ATT: cfg.ATT };
  const missingCounts = Object.fromEntries(
    ["PT", "DEF", "CEN", "ATT"].map((r) => [
      r,
      Math.max(0, requiredCounts[r] - (starters[r] || []).slice(0, requiredCounts[r]).filter(Boolean).length),
    ])
  );
  const totalMissing = Object.values(missingCounts).reduce((a, b) => a + b, 0);

  const rosterByRole = { PT: [], DEF: [], CEN: [], ATT: [] };
  roster.forEach((p) => rosterByRole[p.role]?.push(p));

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(e) => setActiveDrag(e.active?.data?.current?.player || null)}
      onDragCancel={() => setActiveDrag(null)}
      onDragEnd={(e) => { setActiveDrag(null); handleDragEnd(e); }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pitch */}
        <div className="lg:col-span-2 card-surface rounded-3xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Modulo</div>
              <div className="display font-black text-3xl">{module}</div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={module} onValueChange={setModule}>
                <SelectTrigger className="w-32 bg-slate-50 border-slate-200" data-testid="module-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(MODULES).map((m) => (
                    <SelectItem key={m} value={m} data-testid={`module-${m}`}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={save} disabled={busy} className="btn-brand rounded-full" data-testid="save-lineup-btn">
                {busy ? "Salvataggio..." : (totalMissing > 0 ? `Mancano ${totalMissing}` : "Salva formazione")}
              </Button>
            </div>
          </div>
          {totalMissing > 0 && (
            <div className="mb-3 text-xs text-red-400/90" data-testid="missing-summary">
              Formazione incompleta:
              {" "}{Object.entries(missingCounts).filter(([, n]) => n > 0).map(([r, n]) => `${n} ${r}`).join(" · ")}
            </div>
          )}
          <div className="max-w-md mx-auto" data-testid="pitch">
            <SoccerPitch>
              {slots.map((s) => {
                const slotId = `${s.role}-${s.index}`;
                return (
                  <PitchSlot
                    key={slotId}
                    id={slotId}
                    role={s.role}
                    x={s.x}
                    y={s.y}
                    player={slotPlayers[slotId]}
                    invalid={!slotPlayers[slotId]}
                    onRemove={() => removeAt(s.role, s.index)}
                  />
                );
              })}
            </SoccerPitch>
          </div>
        </div>

        {/* Roster */}
        <div className="card-surface rounded-3xl p-4 md:p-6" data-testid="roster-panel">
          <div className="display font-bold text-xl mb-3">Rosa (29)</div>
          {roster.length === 0 ? (
            <p className="text-slate-500 text-sm">La rosa non è ancora stata caricata dall'Admin.</p>
          ) : (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
              {["PT", "DEF", "CEN", "ATT"].map((role) => (
                <div key={role}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1"
                       style={{ color: ROLE_COLORS[role] }}>
                    {role} ({rosterByRole[role].length})
                  </div>
                  <div className="space-y-1.5">
                    {rosterByRole[role].map((p) => (
                      <div key={p.id} className={assignedIds.has(p.id) ? "opacity-40" : ""}>
                        <PlayerChip player={p} dragId={`roster-${p.id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <DragOverlay dropAnimation={{ duration: 250, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" }} zIndex={9999}>
        {activeDrag ? (
          <div
            className="player-chip select-none card-surface rounded-lg px-2 py-1.5 flex items-center gap-2 text-sm shadow-2xl scale-105"
            style={{
              borderColor: `${ROLE_COLORS[activeDrag.role]}88`,
              cursor: "grabbing",
              boxShadow: "0 20px 40px -10px rgba(15,23,42,0.45), 0 8px 16px -8px rgba(15,23,42,0.25)",
              background: "#ffffff",
              transform: "rotate(-2deg)",
            }}
          >
            <span
              className="text-[10px] font-black px-1.5 py-0.5 rounded"
              style={{ background: `${ROLE_COLORS[activeDrag.role]}22`, color: ROLE_COLORS[activeDrag.role] }}
            >
              {activeDrag.role}
            </span>
            <span className="text-slate-900 truncate flex-1">{activeDrag.name}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
